import { useMemo } from 'react';

import { useI18n } from '@change-corgi/core/react/i18n';

import { usePromotePageState } from 'src/app/pages/promote/pageContext';
import { useTracking } from 'src/app/pages/promote/shared/hooks';
import { useLoginState } from 'src/app/shared/hooks/session';

import type { ExpandedPaymentMethod } from '../../../shared/types';

type Result = ModelHookResult<
	{
		name: string;
		firstName: string;
		lastName: string;
		validationErrors: Record<string, string> | null;
		useSplitFields: boolean;
		labelName: string;
		labelFirstName: string;
		labelLastName: string;
	},
	{
		setName: (name: string) => void;
		setFirstName: (firstName: string) => void;
		setLastName: (lastName: string) => void;
		trackNameInputFocus: () => void;
		trackFirstNameInputFocus: () => void;
		trackLastNameInputFocus: () => void;
	}
>;

export function useNameInputWrapper(paymentType: ExpandedPaymentMethod): Result {
	const [{ name, firstName, lastName, validationErrors }, { setName, setFirstName, setLastName }] =
		usePromotePageState();
	const { trackNameInputFocus, trackFirstNameInputFocus, trackLastNameInputFocus } = useTracking();
	const loginState = useLoginState();
	const { translate } = useI18n();
	const isGuest = loginState === 'GUEST';

	const labelName = useMemo(() => {
		if (paymentType === 'creditCard') return translate('fe.components.credit_card.cardholder_name'); // TODO: move i18n label to corgi namespace
		return translate('fe.components.sepa.account_holder_name'); // TODO: move i18n label to corgi namespace
	}, [paymentType, translate]);

	return {
		data: {
			name,
			firstName,
			lastName,
			validationErrors,
			useSplitFields: isGuest,
			labelName,
			labelFirstName: translate('fe.components.credit_card.first_name'),
			labelLastName: translate('fe.components.credit_card.last_name'),
		},
		actions: {
			setName,
			setFirstName,
			setLastName,
			trackNameInputFocus,
			trackFirstNameInputFocus,
			trackLastNameInputFocus,
		},
	};
}
